import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { ProfilePage } from "./routes/dashboard/profile";
import { SettingsAuditPage } from "./routes/dashboard/settings/audit";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";
import { WaypointCallPage } from "./routes/waypoint/$sessionId";
import { WaypointDebriefPage } from "./routes/waypoint/$sessionId.debrief";
import { WaypointHistoryPage } from "./routes/waypoint/history";
import { WaypointIndexPage } from "./routes/waypoint/index";
import { ScenariosPage } from "./routes/waypoint/scenarios";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

// Immersive session route (no shell — own auth guard)
const waypointCallRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/waypoint/$sessionId",
  beforeLoad: requireAuth,
  component: WaypointCallPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/waypoint" });
  },
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: ProfilePage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin(ctx);
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit",
  component: SettingsAuditPage,
});

const waypointIndexRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/waypoint",
  component: WaypointIndexPage,
});

const waypointScenariosRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/waypoint/scenarios",
  component: ScenariosPage,
});

const waypointDebriefRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/waypoint/$sessionId/debrief",
  component: WaypointDebriefPage,
});

const waypointHistoryRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/waypoint/history",
  component: WaypointHistoryPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  // Immersive call route — outside the shell
  waypointCallRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    profileRoute,
    settingsLayout.addChildren([settingsUsersRoute, settingsSystemRoute, settingsAuditRoute]),
    waypointIndexRoute,
    waypointScenariosRoute,
    waypointHistoryRoute,
    waypointDebriefRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
